/** @format */

/**
 * Frontend currency utilities for multi-currency display and conversion.
 */

export const ZERO_DECIMAL_CURRENCIES = new Set([
	"bif",
	"clp",
	"djf",
	"gnf",
	"jpy",
	"kmf",
	"krw",
	"mga",
	"pyg",
	"rwf",
	"ugx",
	"vnd",
	"vuv",
	"xaf",
	"xof",
	"xpf",
]);

/**
 * Three-decimal currencies: amounts in Stripe are expressed in 1/1000 of the major unit.
 * e.g. 1 BHD = 1000 fils → toSmallestUnit(1, "bhd") = 1000
 */
export const THREE_DECIMAL_CURRENCIES = new Set([
	"bhd", // Bahraini Dinar
	"iqd", // Iraqi Dinar
	"jod", // Jordanian Dinar
	"kwd", // Kuwaiti Dinar
	"omr", // Omani Rial
	"tnd", // Tunisian Dinar
]);

/**
 * Maps i18n language codes to their default locale and currency.
 * Used for display-only formatting (e.g. landing page demos).
 */
export const LANGUAGE_CURRENCY_MAP: Record<
	string,
	{ locale: string; currency: string }
> = {
	en: { locale: "en-US", currency: "USD" },
	es: { locale: "es-ES", currency: "EUR" },
	fr: { locale: "fr-FR", currency: "EUR" },
	mx: { locale: "es-MX", currency: "MXN" },
};

export const getLocaleByCurrencyLanguage = (
	language: string,
): { locale: string; currency: string } =>
	LANGUAGE_CURRENCY_MAP[language] ?? LANGUAGE_CURRENCY_MAP["en"];

/**
 * Format a numeric amount as a localized currency string.
 * Uses Intl.NumberFormat for proper symbol and decimal placement.
 *
 * @example formatCurrency(1234.5, "usd") => "$1,234.50"
 * @example formatCurrency(1234.5, "eur", "fr-FR") => "1 234,50 €"
 * @example formatCurrency(1500, "jpy")   => "¥1,500"
 * @example formatCurrency(49.99, "gbp")  => "£49.99"
 */
export const formatCurrency = (
	amount: number,
	currency: string = "usd",
	locale: string = "en-US",
): string => {
	const cur = currency.toUpperCase();
	const curLower = currency.toLowerCase();
	const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(curLower);
	const isThreeDecimal = THREE_DECIMAL_CURRENCIES.has(curLower);
	const digits = isZeroDecimal ? 0 : isThreeDecimal ? 3 : 2;
	return new Intl.NumberFormat(locale, {
		style: "currency",
		currency: cur,
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	}).format(amount);
};

/**
 * Get just the currency symbol for a given currency code.
 *
 * @example getCurrencySymbol("usd") => "$"
 * @example getCurrencySymbol("eur") => "€"
 * @example getCurrencySymbol("gbp") => "£"
 */
export const getCurrencySymbol = (currency: string = "usd"): string => {
	const cur = currency.toUpperCase();
	const formatted = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: cur,
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(0);
	// Strip the "0" to get just the symbol
	return formatted.replace(/[\d,.\s]/g, "").trim();
};

/**
 * Convert a display amount to the smallest currency unit.
 */
export const toSmallestUnit = (amount: number, currency: string): number => {
	if (ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase())) {
		return Math.round(amount);
	}
	return Math.round(amount * 100);
};

/**
 * Convert from smallest currency unit back to display amount.
 */
export const fromSmallestUnit = (
	amountSmallest: number,
	currency: string,
): number => {
	if (ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase())) {
		return amountSmallest;
	}
	return amountSmallest / 100;
};
